import React from 'react';
import { Form } from 'react-bootstrap';
import { getInitials } from './tableUtils';
import { shouldShowAlertsModal } from './enhancedDoubleClickHandler';
import { canEditField } from './schedulePermissions';
import { patchData } from '../../../redux/slices/apiCallSlice';

// Create cell editing handlers
export const createCellEditHandlers = (
  hasEditAccess,
  roles,
  setEditingCell,
  editingCell,
  setEditValue,
  editValue,
  formattedJobs,
  apiUrls,
  logScheduleChange,
  dispatch,
  updateJob,
  setSaveStatus,
  setShowSaveBanner,
  mechOptions,
  elecOptions,
  controlOptions,
  salesOptions,
  // New parameters for alerts functionality
  mappedUserRoles = null,
  pumpDetails = [],
  setShowAlertsModal = null,
  setAlertsJob = null,
  // New parameters for accounting modal
  setShowAccountingModal = null,
  setAccountingJob = null,
  // New parameter for filters (to check moveRequestOnly)
  filters = null
) => {
    // Original sophisticated handleCellDoubleClick function
  const originalHandleCellDoubleClick = (e, job, field) => {
    // Stop event propagation first
    e.stopPropagation();
    
    // Check if moveRequestOnly filter is active and user is not Admin/Scheduling
    if (filters && filters.additionalFilters && filters.additionalFilters.moveRequestOnly) {
      const isAdminOrScheduling = mappedUserRoles && (mappedUserRoles.Admin || mappedUserRoles.Scheduling);
      if (!isAdminOrScheduling) {
        // Disable double-click editing for non-Admin/Scheduling users when moveRequestOnly filter is active
        return;
      }
    }
    
    // Check if user has accounting role and is double-clicking on any cell
    // For accounting users, show the accounting modal regardless of the field clicked
    if (mappedUserRoles && mappedUserRoles.Accounting && !mappedUserRoles.Admin && !mappedUserRoles.Scheduling) {
      if (setShowAccountingModal && setAccountingJob) {
        setAccountingJob(job);
        setShowAccountingModal(true);
        return;
      }
    }
    
    // Prevent editing if user doesn't have permission for this specific field
    if (!hasEditAccess || !canEditField(roles, field, job)) {
      return;
    }
    
    // Save current scroll position before editing
    const tableContainer = document.querySelector('.table-wrapper');
    const scrollPosition = tableContainer ? tableContainer.scrollTop : 0;
    
    // Set editing state with proper value handling for engineer fields
    setEditingCell({ jobId: job.ID, field });
    
    // For engineer fields, find the exact match in dropdown options
    let editValueToSet = job[field] || "";
    if (field === 'MechanicalEngineer' && job[field]) {
      const exactMatch = mechOptions.find(option => option.displayName === job[field]);
      editValueToSet = exactMatch ? exactMatch.displayName : job[field];
    } else if (field === 'ElectricalEngineer' && job[field]) {
      const exactMatch = elecOptions.find(option => option.displayName === job[field]);
      editValueToSet = exactMatch ? exactMatch.displayName : job[field];
    } else if (field === 'ControlsEngineer' && job[field]) {
      const exactMatch = controlOptions.find(option => option.displayName === job[field]);
      editValueToSet = exactMatch ? exactMatch.displayName : job[field];
    } else if (field === 'SalesRep' && job[field]) {
      const salesList = Array.isArray(salesOptions) ? salesOptions : [];
      const exactMatch = salesList.find(option => option.displayName === job[field]);
      editValueToSet = exactMatch ? exactMatch.displayName : job[field];
    } 
    
    setEditValue(editValueToSet);
    
    // Store scroll position as a data attribute
    if (tableContainer) {
      tableContainer.setAttribute('data-scroll-position', scrollPosition);
    }
  };
  
  const handleEditChange = (e) => {
    setEditValue(e.target.value);
  };
  
  const handleEditBlur = async () => {
    // Prevent editing if user doesn't have permission
    if (!hasEditAccess) return;
    
    // Get the stored scroll position
    const tableContainer = document.querySelector('.table-wrapper');
    const scrollPosition = tableContainer ? 
      parseInt(tableContainer.getAttribute('data-scroll-position') || '0', 10) : 0;
    
    try {
      if (editingCell.jobId && editingCell.field) {
        const job = formattedJobs.find(job => job.ID === editingCell.jobId);
        
        if (job && job[editingCell.field] !== editValue) {
          // Store the previous value for the change log
          const prevValue = job[editingCell.field];
          
          // Handle the value based on field type
          let valueToSave = editValue;
          
          // For dollar value, make sure we strip any currency formatting before saving,
          // but keep it as a string since the API expects a string
          if (editingCell.field === 'DollarValue') {
            // Remove any currency symbols, commas, etc. before saving, but keep as string
            valueToSave = editValue.replace(/[^0-9.-]/g, '');
          }
          
          // For PUT requests, include the original field name and ID
          const putData = {
            ID: job.ID,  // Keep ID capitalized
            [editingCell.field]: valueToSave // Keep original field name capitalization
          };
            // Make API call using PATCH method with the ID in the URL
          await dispatch(
            patchData({
              url: `${apiUrls.generalProject}?id=${job.ID}`,
              data: putData,
            })
          ).unwrap();
          
          // Only proceed with logging and Redux update if the main API call succeeded
          {
            // Log the change to the schedule change log
            try {
              await logScheduleChange(
                job.ID,
                job.JobNumber,
                job.ProjectName,
                editingCell.field,
                prevValue,
                valueToSave
              );
            } catch (logError) {
              console.warn('Main update succeeded but logging failed:', logError);
              // Don't throw here - the main update worked
            }
            
            // Create a local update for Redux with only the necessary information
            const localUpdate = { 
              ID: job.ID,
              [editingCell.field]: valueToSave
            };
            
            // Update Redux store with the specific field that was updated
            dispatch(updateJob(localUpdate));
            
            console.log(`Updated ${editingCell.field} for job ${job.ID} to "${valueToSave}"`);
            
            // Show success notification
            setSaveStatus(`Successfully updated ${editingCell.field}`);
            setShowSaveBanner(true);
              setTimeout(() => {
              setShowSaveBanner(false);
            }, 3000);
          }
        }
      }
    } catch (error) {
      console.error('Error saving changes:', error);
      
      // Show error notification
      setSaveStatus(`Failed to update: ${error.message}`);
      setShowSaveBanner(true);
      
      // Hide the notification after 5 seconds
      setTimeout(() => {
        setShowSaveBanner(false);
      }, 5000);
    } finally {
      // Reset editing state
      setEditingCell({ jobId: null, field: null });
      
      // Restore scroll position after a short delay to ensure rendering is complete
      setTimeout(() => {
        if (tableContainer) {
          tableContainer.scrollTop = scrollPosition;
        }
      }, 10);
    }
  };
  
  const handleEditKeyDown = (e) => {
    if (e.key === 'Enter') {
      handleEditBlur();
    } else if (e.key === 'Escape') {
      // Get the stored scroll position
      const tableContainer = document.querySelector('.table-wrapper');
      const scrollPosition = tableContainer ? 
        parseInt(tableContainer.getAttribute('data-scroll-position') || '0', 10) : 0;
      
      // Reset editing state
      setEditingCell({ jobId: null, field: null });
      
      // Restore scroll position
      setTimeout(() => {
        if (tableContainer) {
          tableContainer.scrollTop = scrollPosition;
        }
      }, 10);
    }
  };
  
  const renderEditableCell = (job, field, content) => {
    const isEditing = editingCell.jobId === job.ID && editingCell.field === field;

    const normalizeDateInputValue = (value) => {
      if (!value) {
        return '';
      }

      const stringValue = String(value).trim();
      if (!stringValue) {
        return '';
      }

      // Already in yyyy-mm-dd format.
      if (/^\d{4}-\d{2}-\d{2}$/.test(stringValue)) {
        return stringValue;
      }

      // Handles values like 2026-04-22T00:00:00.000Z.
      if (/^\d{4}-\d{2}-\d{2}T/.test(stringValue)) {
        return stringValue.slice(0, 10);
      }

      // Handles common mm/dd/yyyy values shown in the table.
      const mmddyyyy = stringValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (mmddyyyy) {
        const month = mmddyyyy[1].padStart(2, '0');
        const day = mmddyyyy[2].padStart(2, '0');
        const year = mmddyyyy[3];
        return `${year}-${month}-${day}`;
      }

      const parsed = new Date(stringValue);
      if (!Number.isNaN(parsed.getTime())) {
        return parsed.toISOString().slice(0, 10);
      }

      return '';
    };
    
    if (isEditing && hasEditAccess) {      // Handle engineer dropdown fields
      if (field === 'MechanicalEngineer') {
        // Check if the current value exists in options
        const hasMatchingOption = mechOptions.some(option => option.displayName === editValue);
        
        return (
          <div className="editing-cell" style={{ padding: 0, margin: 0, height: '100%', width: '100%' }}>
            <Form.Select
              value={editValue}
              onChange={handleEditChange}
              onBlur={handleEditBlur}
              onKeyDown={handleEditKeyDown}
              autoFocus
              className="form-control form-control-sm"
              style={{ width: '100%', height: '100%', padding: '4px', border: '1px solid #007bff' }}
            >
              <option value="">Select...</option>
              {mechOptions.map((option) => (
                <option key={option.displayName} value={option.displayName}>
                  {option.displayName}
                </option>
              ))}
              {!hasMatchingOption && editValue && (
                <option key="current-value" value={editValue} style={{ color: 'red' }}>
                  *{editValue}
                </option>
              )}
            </Form.Select>
          </div>
        );
      }
      
      if (field === 'ElectricalEngineer') {
        // Check if the current value exists in options
        const hasMatchingOption = elecOptions.some(option => option.displayName === editValue);
        
        return (
          <div className="editing-cell" style={{ padding: 0, margin: 0, height: '100%', width: '100%' }}>
            <Form.Select
              value={editValue}
              onChange={handleEditChange}
              onBlur={handleEditBlur}
              onKeyDown={handleEditKeyDown}
              autoFocus
              className="form-control form-control-sm"
              style={{ width: '100%', height: '100%', padding: '4px', border: '1px solid #007bff' }}
            >
              <option value="">Select...</option>
              {elecOptions.map((option) => (
                <option key={option.displayName} value={option.displayName}>
                  {option.displayName}
                </option>
              ))}
              {!hasMatchingOption && editValue && (
                <option key="current-value" value={editValue} style={{ color: 'red' }}>
                  *{editValue}
                </option>
              )}
            </Form.Select>
          </div>
        );
      }
      
      if (field === 'ControlsEngineer') {
        // Check if the current value exists in options
        const hasMatchingOption = controlOptions.some(option => option.displayName === editValue);
        
        return (
          <div className="editing-cell" style={{ padding: 0, margin: 0, height: '100%', width: '100%' }}>
            <Form.Select
              value={editValue}
              onChange={handleEditChange}
              onBlur={handleEditBlur}
              onKeyDown={handleEditKeyDown}
              autoFocus
              className="form-control form-control-sm"
              style={{ width: '100%', height: '100%', padding: '4px', border: '1px solid #007bff' }}
            >
              <option value="">Select...</option>
              {controlOptions.map((option) => (
                <option key={option.displayName} value={option.displayName}>
                  {option.displayName}
                </option>
              ))}
              {!hasMatchingOption && editValue && (
                <option key="current-value" value={editValue} style={{ color: 'red' }}>
                  *{editValue}
                </option>
              )}
            </Form.Select>
          </div>
        );
      }
      
      // Handle SalesRep dropdown (same as engineers, use mechOptions for now or a dedicated salesRepOptions if available)
      if (field === 'SalesRep') {
        const options = salesOptions || [];
        const hasMatchingOption = options.some(option => option.displayName === editValue);
        return (
          <div className="editing-cell" style={{ padding: 0, margin: 0, height: '100%', width: '100%' }}>
            <Form.Select
              value={editValue}
              onChange={handleEditChange}
              onBlur={handleEditBlur}
              onKeyDown={handleEditKeyDown}
              autoFocus
              className="form-control form-control-sm"
              style={{ width: '100%', height: '100%', padding: '4px', border: '1px solid #007bff' }}
            >
              <option value="">Select...</option>
              {options.map((option) => (
                <option key={option.displayName} value={option.displayName}>
                  {option.displayName}
                </option>
              ))}
              {!hasMatchingOption && editValue && (
                <option key="current-value" value={editValue} style={{ color: 'red' }}>
                  *{editValue}
                </option>
              )}
            </Form.Select>
          </div>
        );
      }
      
      // Determine if this is a date field
      const isDateField = field.includes('Date') || 
                         field.includes('Due') || 
                         field.includes('Done') || 
                         field.includes('Completion') ||
                         field === 'OrderReceived' ||
                         field === 'ProductionRelease';
      
      // Determine if this is a dollar value field
      const isDollarValue = field === 'DollarValue';
      
      // Always use native date input for date fields so the picker is available.
      const inputType = isDateField ? "date" : "text";
      
      let inputValue = editValue;
      if (isDollarValue && typeof editValue === 'string') {
        // Strip currency formatting for editing
        inputValue = editValue.replace(/[^0-9.-]/g, '');
      } else if (isDateField) {
        inputValue = normalizeDateInputValue(editValue);
      }
      
      return (
        <div className="editing-cell" style={{ 
          padding: 0, 
          margin: 0, 
          height: '100%', 
          width: '100%'
        }}>
          <input
            type={inputType}
            value={inputValue}
            onChange={handleEditChange}
            onBlur={handleEditBlur}
            onKeyDown={handleEditKeyDown}
            autoFocus
            className="form-control form-control-sm"
            style={{ 
              width: isDateField ? '150px' : '100%', // Expanded width for date fields
              height: '100%', 
              padding: '8px',
              border: '1px solid #007bff',
              boxSizing: 'border-box'
            }}
          />
        </div>
      );
    }
      // For non-editing state, determine if the content is empty
    // Specific fields that should remain blank instead of showing "N/A"
    const fieldsToStayBlank = [
      "TransportType", 
      "ReleaseDate",
      "CurrentPlanShipDate", 
      "ActualShipDate", 
      "ScheduledArrivalDate", 
      "SalesRep",
      // Done fields - should show blank instead of N/A
      "MechanicalDone",
      "ElectricalDone",
      "ControlsDone",
      "EngineeringDone",
      "NPPipeFabDone",
      "FabDone",
      "SoftwareDone",
      "ControlPanelDone",
      "SoftwareType",
      "HCPumpsDone",
      "PumpDone",
      "PumpsDone", // Alternative name for PumpDone
      "AssemblyDone",
      "TestDone",
      "NPCSMDone",
      "NPSuctionLineDone",
      "NPInducerTubeDone",
      "NPBMXDone",
      "PackDone",
      "MechanicalLongLeadsDone",
      "ElectricalLongLeadsDone",
      // Expected Completion fields - should show blank instead of N/A
      "ControlPanelExpectedCompletion",
      "AssemblyExpectedCompletion",
      "TestExpectedCompletion",
      "FabExpectedCompletion",
      "PumpExpectedCompletion"
    ];
      if (content === undefined || content === null || content === "") {
      // If it's a field that should stay blank, return an empty string
      if (fieldsToStayBlank.includes(field)) {
        return "";
      }
      // For all other fields, show "N/A"
      return "N/A";
    }
    
    // For engineer fields in non-editing mode, show initials instead of full name
    const engineerFields = ['MechanicalEngineer', 'ElectricalEngineer', 'ControlsEngineer', 'SalesRep'];
    if (engineerFields.includes(field)) {
      // Always show two-letter initials for multi-word names, uppercased
      return getInitials(content).toUpperCase();
    }
    
    return content;
  };
  
  // Enhanced handleCellDoubleClick with role-based alerts logic
const createEnhancedHandleCellDoubleClick = (originalHandler, mappedUserRoles, pumpDetails, setShowAlertsModal, setAlertsJob) => {
  return (e, job, field) => {
    console.log('=== Enhanced handleCellDoubleClick ===');
    console.log('User roles:', mappedUserRoles);
    console.log('Job:', job);
    console.log('Field:', field);
    
    // IMPORTANT: Check for accounting role first before alerts modal
    // Accounting users should get their specialized modal, not the alerts modal
    if (mappedUserRoles && mappedUserRoles.Accounting && !mappedUserRoles.Admin && !mappedUserRoles.Scheduling) {
      console.log('✅ Accounting user - letting original handler manage this');
      // Let the original handler deal with accounting modal logic
      if (originalHandler) {
        originalHandler(e, job, field);
      }
      return;
    }
    
    const canEditClickedField = hasEditAccess && canEditField(roles, field, job);

    // Admin/Scheduling keep direct edit behavior. Limited-edit roles, such as
    // EngineerManager, should still see notices when the clicked field is read-only.
    const hasFullEditAccess = mappedUserRoles.Admin || mappedUserRoles.Scheduling;
    
    if (!hasFullEditAccess && !canEditClickedField && shouldShowAlertsModal(mappedUserRoles, job, pumpDetails)) {
      console.log('✅ Showing alerts modal for non-privileged user');
      e.preventDefault();
      e.stopPropagation();
      setAlertsJob(job);
      setShowAlertsModal(true);
      return;
    }
    
    console.log('Proceeding with normal edit behavior');
    
    // Proceed with original sophisticated editing logic
    if (originalHandler) {
      originalHandler(e, job, field);
    }
  };  };

  // Create enhanced version that includes alerts functionality
  const handleCellDoubleClick = (mappedUserRoles && setShowAlertsModal && setAlertsJob) 
    ? createEnhancedHandleCellDoubleClick(originalHandleCellDoubleClick, mappedUserRoles, pumpDetails, setShowAlertsModal, setAlertsJob)
    : originalHandleCellDoubleClick;

  return {
    handleCellDoubleClick,
    handleEditChange,
    handleEditBlur,
    handleEditKeyDown,
    renderEditableCell
  };
};
